// src/pages/BestBetsPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useFeatureAccess } from '../contexts/SubscriptionContext';
import { useSelectedState } from '../contexts/SelectedStateContext';
import BestBetCard from '../components/BestBetCard';
import RecalculatingState from '../components/RecalculatingState';
import { useAvailableSports } from '../hooks/useAvailableSports';
import { ENTITLEMENT_KEYS } from '../config/entitlements';
import { buildLockedOverlay, shouldLockPreviewItem } from '../services/featureGateService';
import './BestBetsPage.css';
import PageSeo from '../components/PageSeo';
import { getPageSeo } from '../utils/seoConfig';

// ─── Constants ─────────────────────────────────────────────────────────────────
const API_URL = '/best-bets/';

// Cloudflare Stream video_id for the Best Bets walkthrough lesson in the Academy.
const BEST_BETS_VIDEO_ID = 'a327623765a32be1a73a9862999d3003';

const SORT_OPTIONS = [
  { value: 'ev_best',       label: 'Best EV (Global)' },
  { value: 'ev_state',      label: 'State EV' },
  { value: 'ev_bookmaker',  label: 'Bookmaker EV' },
];

// ─── Rank-by filter ─────────────────────────────────────────────────────────
// Top-level ordering axis, independent of the EV-source dropdown above.
// 'ev' preserves the existing EV-ranked behavior (default). 'bet_grade' and
// 'date' re-order the same fetched page client-side — the backend has no
// sort_by=signal_score option, so we rank the already-filtered/limited page
// by signal_score instead, and 'date' additionally scopes the request to a
// single calendar day via the API's `date` param and orders chronologically.
const RANK_MODES = [
  { value: 'ev',        label: 'EV' },
  { value: 'bet_grade', label: 'Bet Grade' },
  { value: 'date',      label: 'Date' },
];

// Quick day picker shown only in 'date' rank mode, mirroring the backend's
// accepted `date` keywords. 'custom' switches to an explicit YYYY-MM-DD input.
const DATE_QUICK_OPTIONS = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today',     label: 'Today' },
  { value: 'tomorrow',  label: 'Tomorrow' },
];

const PAGE_SIZE = 20;

const PRIORITY_SPORTS = [
  'americanfootball_nfl',
  'basketball_nba',
  'americanfootball_ncaaf',
  'soccer_fifa_world_cup',
  'icehockey_nhl',
  'baseball_mlb',
  'basketball_wnba',
  'mma_mixed_martial_arts',
  'soccer_usa_mls',
  'soccer_epl',
  'soccer_france_ligue_one',
  'soccer_germany_bundesliga',
  'soccer_spain_la_liga',
  'soccer_italy_serie_a',
];

// ─── Soccer grouping ────────────────────────────────────────────────────────
// All soccer_* sport codes are consolidated into a single "⚽ Soccer" entry in
// the primary sport filter. Selecting it activates a secondary filter bar with
// the individual soccer leagues, derived dynamically from whatever soccer_*
// codes the API currently reports (no hardcoded league list). `selectedSport`
// remains the single source of truth: it holds either the sentinel group
// value (combined view) or a specific soccer_* code (one league picked from
// the secondary bar) — no parallel filtering state is introduced.
const SOCCER_SPORT_PREFIX = 'soccer_';
const SOCCER_GROUP_VALUE = 'soccer';
// Over-fetch window for the combined "All Soccer" view so leagues that don't
// crack the global top page still surface once filtered down client-side.
const SOCCER_AGGREGATE_LIMIT = 200;

function isSoccerSport(sport) {
  return typeof sport === 'string' && sport.startsWith(SOCCER_SPORT_PREFIX);
}

// ─── Market filter ──────────────────────────────────────────────────────────
// Stored `market` values are h2h / spreads / totals for the three "main"
// markets, and anything else (player_points, batter_hits, etc.) is a prop.
// The backend only supports a binary market_category=main|props split, so
// isolating one specific main-market type is done the same way as the
// Soccer group above: request the broader `main` category, over-fetch a
// larger window, then filter down to the exact value client-side.
const MAIN_MARKET_VALUES = ['h2h', 'spreads', 'totals'];
const MARKET_OPTIONS = [
  { value: 'all',     label: 'All Markets' },
  { value: 'h2h',     label: 'Moneylines' },
  { value: 'spreads', label: 'Spreads' },
  { value: 'totals',  label: 'Totals' },
  { value: 'props',   label: 'Player Props' },
];
const MARKET_AGGREGATE_LIMIT = 200;

function isMainMarketValue(market) {
  return MAIN_MARKET_VALUES.includes(market);
}

// Sport-tab cache TTL: entries older than this threshold are re-fetched.
// Prevents a single partial/stale response from poisoning the cache for the
// entire browser session.
const SPORT_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL',
  basketball_nba: 'NBA',
  americanfootball_ncaaf: 'NCAAF',
  icehockey_nhl: 'NHL',
  baseball_mlb: 'MLB',
  basketball_wnba: 'WNBA',
  soccer_fifa_world_cup: 'FIFA World Cup',
  mma_mixed_martial_arts: 'MMA',
  baseball_milb: 'Minor League Baseball',
  baseball_ncaa: 'College Baseball',
  basketball_ncaab: 'NCAA Basketball',
  soccer_usa_mls: 'MLS',
  soccer_epl: 'Premier League',
  soccer_france_ligue_one: 'Ligue 1',
  soccer_germany_bundesliga: 'Bundesliga',
  soccer_spain_la_liga: 'La Liga',
  soccer_italy_serie_a: 'Serie A',
  boxing_boxing: 'Boxing',

};

// Single source of truth: label (display), key (Odds API key), title (exact stored name for JSONB sort), icon
const BOOKMAKERS = [
  { label: 'DraftKings',       key: 'draftkings',     title: 'DraftKings',         icon: '/images/draftkings_icon.png' },
  { label: 'FanDuel',          key: 'fanduel',        title: 'FanDuel',            icon: '/images/fanduel_icon.png' },
  { label: 'BetMGM',           key: 'betmgm',         title: 'BetMGM',             icon: '/images/bet_mgm_icon.png' },
  { label: 'Caesars',          key: 'williamhill_us', title: 'Caesars',            icon: '/images/caesars_icon.png' },
  { label: 'Hard Rock Bet',    key: 'hardrockbet',    title: 'Hard Rock Bet',      icon: '/images/hard_rock_bet_icon.png' },
  //{ label: 'Hard Rock Bet (AZ)', key: 'hardrockbet_az', title: 'Hard Rock Bet (AZ)', icon: '/images/hard_rock_bet_icon.png' },
  //{ label: 'Hard Rock Bet (FL)', key: 'hardrockbet_fl', title: 'Hard Rock Bet (FL)', icon: '/images/hard_rock_bet_icon.png' },
  //{ label: 'Hard Rock Bet (OH)', key: 'hardrockbet_oh', title: 'Hard Rock Bet (OH)', icon: '/images/hard_rock_bet_icon.png' },
  { label: 'ESPN BET',         key: 'espnbet',        title: 'theScore Bet',           icon: '/images/espn_bet_icon.png' },
  { label: 'BetRivers',        key: 'betrivers',      title: 'BetRivers',          icon: '/images/betrivers_icon.png' },
  { label: 'Fanatics',         key: 'fanatics',       title: 'Fanatics',           icon: '/images/fanatics_icon.png' },
  { label: 'Bally Bet',        key: 'ballybet',       title: 'Bally Bet',          icon: '/images/bally_bet_icon.png' },
  { label: 'BetParx',          key: 'betparx',        title: 'BetParx',            icon: '/images/betparx.jpeg' },
  { label: 'BetUS',            key: 'betus',          title: 'BetUS',              icon: '/images/betus.jpeg' },
  { label: 'Bovada',           key: 'bovada',         title: 'Bovada',             icon: '/images/bovada.webp' },
  { label: 'LowVig',           key: 'lowvig',         title: 'LowVig.ag',             icon: '/images/lowvig.jpg' },
  { label: 'NoVig',            key: 'novig',          title: 'Novig',              icon: '/images/novig.png' },
  { label: 'Rebet',            key: 'rebet',          title: 'ReBet',              icon: '/images/rebet.jpg' },
  { label: 'Pick6',            key: 'pick6',          title: 'DraftKings Pick6',   icon: '/images/pick6.png' },
  { label: 'PrizePicks',       key: 'prizepicks',     title: 'PrizePicks',         icon: '/images/prizepicks.jpg' },
  { label: 'Underdog',         key: 'underdog',       title: 'Underdog',   icon: '/images/underdog.png' },
  //{ label: 'Kalshi',           key: 'kalshi',         title: 'Kalshi',             icon: '/images/kalshi.png' },
  { label: 'Polymarket',       key: 'polymarket',     title: 'Polymarket',         icon: '/images/polymarket.png' },
  { label: 'ProphetX',         key: 'prophetx',       title: 'ProphetX',           icon: '/images/prophetx.png' },
];

// Icon map keyed by API key, display label, and stored title so card lookups
// work regardless of what format the backend returns.
const BOOKMAKER_ICONS = Object.fromEntries(
  BOOKMAKERS.flatMap(b =>
    b.icon ? [[b.key, b.icon], [b.label, b.icon], [b.title, b.icon]] : []
  )
);

// Normalise whatever the backend returns (key, label, or stored title) to a canonical key.
const BOOKMAKER_KEY_LOOKUP = Object.fromEntries(
  BOOKMAKERS.flatMap(({ key, label, title }) => [
    [key.toLowerCase(),   key],
    [label.toLowerCase(), key],
    [title.toLowerCase(), key],
  ])
);

// Map from Odds API key to exact stored title (case-sensitive JSONB sort key).
const BOOKMAKER_TITLE_BY_KEY = Object.fromEntries(
  BOOKMAKERS.map(({ key, title }) => [key, title])
);

function normalizeBookmakerKey(value) {
  if (!value) return null;
  return BOOKMAKER_KEY_LOOKUP[String(value).toLowerCase()] ?? String(value).toLowerCase();
}

function formatSportLabel(sport) {
  if (!sport) return '';
  if (sport === SOCCER_GROUP_VALUE) return '⚽ Soccer';

  const leagues = {
    americanfootball_nfl: 'NFL',
    basketball_nba: 'NBA',
    americanfootball_ncaaf: 'NCAAF',
    icehockey_nhl: 'NHL',
    baseball_mlb: 'MLB',
    basketball_wnba: 'WNBA',
    soccer_fifa_world_cup: 'FIFA World Cup',
    mma_mixed_martial_arts: 'MMA',
    baseball_milb: 'Minor League Baseball',
    baseball_ncaa: 'College Baseball',
    basketball_ncaab: 'NCAA Basketball',
    soccer_usa_mls: 'MLS',
    soccer_epl: 'Premier League',
    soccer_france_ligue_one: 'Ligue 1',
    soccer_germany_bundesliga: 'Bundesliga',
    soccer_spain_la_liga: 'La Liga',
    soccer_italy_serie_a: 'Serie A',
    boxing_boxing: 'Boxing',
    icehockey_liiga: 'Finnish Liiga Hockey',


  };

  if (leagues[sport]) return leagues[sport];

  const lowerSport = sport.toLowerCase();

  if (lowerSport.includes('tennis_atp')) {
    return "Men's Tennis";
  }

  if (lowerSport.includes('wta')) {
    return "Women's Tennis";
  }

  return sport
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatMarket(market) {
  if (!market) return '';
  let s = market.startsWith('player_') ? market.slice('player_'.length) : market;
  if (s.startsWith('batter_')) s = s.slice('batter_'.length);
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function fmtOdds(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '–';
  return n > 0 ? `+${n}` : `${n}`;
}

// ─── Main fetch (used as the useQuery queryFn below) ────────────────────────
async function fetchBestBetsPageData({
  sortBy, sideFilter, marketFilter, stateFilter, bookmakerFilter, selectedSport, rankMode, dateFilter, page, debouncedSearch, signal,
}) {
  const params = {
    sort_by:    sortBy,
    sort_order: 'desc',
    limit:      PAGE_SIZE + 1,   // fetch one extra to detect next page
    offset:     (page - 1) * PAGE_SIZE,
  };

  // ── Date rank mode ─────────────────────────────────────────────────────
  // Scopes the request to a single calendar day; the backend resolves
  // "today"/"tomorrow"/"yesterday" or an explicit YYYY-MM-DD itself.
  if (rankMode === 'date' && dateFilter) {
    params.date = dateFilter;
  }

  // ── Sport filter ───────────────────────────────────────────────────────
  // Combined "All Soccer" view — backend only supports filtering by one
  // exact sport code, not a group/prefix, so over-fetch a larger window and
  // filter down to soccer_* sports client-side below instead.
  const soccerAggregate = selectedSport === SOCCER_GROUP_VALUE;
  if (soccerAggregate) {
    params.limit  = SOCCER_AGGREGATE_LIMIT;
    params.offset = 0;
  } else if (selectedSport !== 'all') {
    params.sport = selectedSport;
  }

  // ── Side filter ────────────────────────────────────────────────────────
  if (sideFilter !== 'all') {
    params.bet_type = sideFilter.charAt(0).toUpperCase() + sideFilter.slice(1);
  }

  // ── Market filter ──────────────────────────────────────────────────────
  // 'props' maps 1:1 onto the backend's market_category=props. Isolating a
  // single main-market type (h2h/spreads/totals) has no exact backend
  // param, so request market_category=main and over-fetch, then filter down
  // client-side below (same approach as the Soccer group aggregate).
  const marketNeedsClientFilter = isMainMarketValue(marketFilter);
  if (marketFilter === 'props') {
    params.market_category = 'props';
  } else if (marketNeedsClientFilter) {
    params.market_category = 'main';
    params.limit  = MARKET_AGGREGATE_LIMIT;
    params.offset = 0;
  }

  // ── Sort-specific params ───────────────────────────────────────────────
  if (sortBy === 'ev_state') {
    params.state = stateFilter;
  } else if (sortBy === 'ev_bookmaker') {
    params.bookmaker = BOOKMAKER_TITLE_BY_KEY[bookmakerFilter];
  }
  // ev_best: no extra params — backend ranks by global EV across all books.

  // When the Soccer group is active with no specific league chosen, scope
  // the over-fetched results down to soccer_* sports before normalizing.
  const filterSoccerIfNeeded = items => {
    if (!soccerAggregate) return items;
    const hasSportField = items.some(item => item.sport != null);
    if (!hasSportField) {
      console.warn('BestBetsPage: expected a `sport` field on /best-bets/ results to filter the Soccer group; got none. Showing unfiltered results.');
      return items;
    }
    return items.filter(item => isSoccerSport(item.sport));
  };

  // When a single main-market type is selected, scope the over-fetched
  // `market_category=main` results down to that exact `market` value.
  const filterMarketIfNeeded = items => {
    if (!marketNeedsClientFilter) return items;
    return items.filter(item => item.market === marketFilter);
  };

  // Helper: normalise a single API result item per current sortBy
  const normalizeItem = item => {
    let selectedEv, selectedPrice, selectedBookmaker;
    if (sortBy === 'ev_state') {
      selectedEv        = item.state_ev;
      selectedPrice     = item.state_best_price;
      selectedBookmaker = item.state_best_bookmaker;
    } else if (sortBy === 'ev_bookmaker') {
      selectedEv        = item.bookmaker_ev;
      selectedPrice     = item.bookmaker_price;
      selectedBookmaker = BOOKMAKER_TITLE_BY_KEY[bookmakerFilter];
    } else {
      selectedEv        = item.ev_best;
      selectedPrice     = item.best_price;
      selectedBookmaker = item.best_bookmaker;
    }
    return {
      ...item,
      expected_value:  selectedEv,
      price:           selectedPrice,
      bookmaker:       selectedBookmaker,
      bookmaker_key:   normalizeBookmakerKey(selectedBookmaker),
      price_HardRock:
        item.price_HardRock ??
        item.bookmaker_prices?.['Hard Rock Bet'] ??
        item.bookmaker_prices?.HardRock ??
        null,
      HR_Juice: item.HR_Juice ?? item.state_vig ?? null,
    };
  };

  // Re-order the already-filtered/limited page per the selected rank mode.
  // There's no backend sort_by=signal_score, so 'bet_grade' ranks the top
  // however-many bets we fetched by their signal_score instead of EV.
  // 'date' already scopes the request to a single day via params.date above —
  // within that day, still rank by highest EV (not chronologically).
  const applyRankMode = items => {
    if (rankMode === 'bet_grade') {
      return [...items].sort((a, b) => (Number(b.signal_score) || 0) - (Number(a.signal_score) || 0));
    }
    if (rankMode === 'date') {
      return [...items].sort((a, b) => (Number(b.expected_value) || 0) - (Number(a.expected_value) || 0));
    }
    return items;
  };

  if (debouncedSearch) {
    // ── Search mode: two parallel requests (player + team) ──────────────
    // /best-bets/ can be slow to assemble; give it more room than the
    // default 10s timeout so search results aren't flagged as a failure.
    const [playerRes, teamRes] = await Promise.all([
      api.get(API_URL, { params: { ...params, player: debouncedSearch }, timeout: 30_000, signal }),
      api.get(API_URL, { params: { ...params, team:   debouncedSearch }, timeout: 30_000, signal }),
    ]);
    const playerResults = Array.isArray(playerRes.data?.results) ? playerRes.data.results : [];
    const teamResults   = Array.isArray(teamRes.data?.results)   ? teamRes.data.results   : [];

    // Merge then deduplicate by stable key before normalization
    const seen = new Set();
    const merged = [...playerResults, ...teamResults].filter(item => {
      const k =
        `${item.game_id || ''}-${item.player || item.team || ''}-` +
        `${item.market || ''}-${item.bet_type || ''}-${item.point_line ?? ''}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    const scopedMerged = filterMarketIfNeeded(filterSoccerIfNeeded(merged));
    const valid = applyRankMode(scopedMerged.map(normalizeItem).filter(item => item.price != null));
    return { valid, hasMore: false }; // pagination not shown in search mode
  }

  // ── Normal mode: single request ────────────────────────────────────────
  // Same extended timeout as the search-mode requests above.
  const res = await api.get(API_URL, { params, timeout: 30_000, signal });
  const results = Array.isArray(res.data?.results) ? res.data.results : [];
  const scoped  = filterMarketIfNeeded(filterSoccerIfNeeded(results));
  // The combined Soccer view and single-main-market view over-fetch and
  // filter client-side, so "next page" detection against PAGE_SIZE no longer
  // reflects the true remaining count — disable pagination for them (same
  // approach as search mode above).
  const more  = (soccerAggregate || marketNeedsClientFilter) ? false : scoped.length > PAGE_SIZE;
  const valid = applyRankMode(scoped.slice(0, PAGE_SIZE).map(normalizeItem).filter(item => item.price != null));
  return { valid, hasMore: more };
}



// ─── Main Page ───────────────────────────────────────────────────────────────────
export default function BestBetsPage() {  const { state: stateParam } = useParams();
  const navigate = useNavigate();  const bestBetsAccess = useFeatureAccess(ENTITLEMENT_KEYS.BEST_BETS_FULL);
  const { selectedState: savedSelectedState } = useSelectedState();
  // Note: the /value-finder (no state) route redirects via ValueFinderRedirect
  // in App.js before this component ever mounts, so stateParam is always set.
  // Deep-link presets, e.g. /value-finder/oh?sport=americanfootball_nfl or
  // ?sortBy=ev_bookmaker&bookmaker=fanduel — read once on mount so other
  // pages/CTAs can link straight into a specific filter combination.
  const [searchParams] = useSearchParams();

  // ── Filter state ────────────────────────────────────────────────────────────
  const [selectedSport,    setSelectedSport]    = useState(() => searchParams.get('sport') || 'all');
  const [sideFilter,       setSideFilter]       = useState('all'); // all | over | under
  const [marketFilter,     setMarketFilter]     = useState('all'); // all | h2h | spreads | totals | props
  const [sortBy,           setSortBy]           = useState(() => {
    const requestedSort = searchParams.get('sortBy');
    if (SORT_OPTIONS.some(o => o.value === requestedSort)) return requestedSort;
    const savedState = stateParam?.toUpperCase() || savedSelectedState;
    return savedState ? 'ev_state' : 'ev_best';
  });

  // ── Rank-by filter: EV (default) / Bet Grade (signal_score) / Date ──────────
  const [rankMode, setRankMode] = useState(() => {
    const requested = searchParams.get('rankMode');
    return RANK_MODES.some(o => o.value === requested) ? requested : 'ev';
  });
  // Quick day picker for 'date' rank mode — 'today' | 'tomorrow' | 'yesterday'
  // or an explicit 'YYYY-MM-DD', sent straight through as the API's `date` param.
  const [dateFilter, setDateFilter] = useState('today');

  const [page,             setPage]             = useState(1);

  // ev_state controls — pre-populate from URL param or saved state
  const [stateFilter, setStateFilter] = useState(
    () => stateParam?.toUpperCase() || savedSelectedState
  );

  // Sync stateFilter when URL param changes (back/forward navigation)
  useEffect(() => {
    if (stateParam) {
      const upper = stateParam.toUpperCase();
      setStateFilter(upper);
      setPage(1);
    }
  }, [stateParam]);

  // SEO is handled declaratively via <PageSeo> in the JSX return below.

  // ev_bookmaker controls
  // FIX #3: Single controlled value; no My Books / All Books toggle.
  // Preset via ?bookmaker=<key|label|title> (normalized against BOOKMAKERS),
  // falling back to DraftKings when absent or unrecognized.
  const [bookmakerFilter, setBookmakerFilter] = useState(() => {
    const requested = normalizeBookmakerKey(searchParams.get('bookmaker'));
    return BOOKMAKERS.some(b => b.key === requested) ? requested : 'draftkings';
  });

  // Sport tabs — shared cache so Home/Value Finder don't each issue their own request.
  const { sportKeys: knownSports, loading: sportsLoading } = useAvailableSports();

  // Search filter
  const [searchQuery,     setSearchQuery]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ── Search / Bookmaker lock overlays (non-subscribers cannot use these) ────
  const [showSearchLock, setShowSearchLock] = useState(false);
  const [showBookLock,   setShowBookLock]   = useState(false);

  // Card expansion
  const [expandedIds, setExpandedIds] = useState(new Set());


  const orderedSports = React.useMemo(() => {
    let soccerGroupAdded = false;
    const priorityOrdered = [];
    for (const s of PRIORITY_SPORTS) {
      if (isSoccerSport(s)) {
        if (!soccerGroupAdded && knownSports.some(isSoccerSport)) {
          priorityOrdered.push(SOCCER_GROUP_VALUE);
          soccerGroupAdded = true;
        }
        continue;
      }
      if (knownSports.includes(s)) priorityOrdered.push(s);
    }
    const rest = knownSports
      .filter(s => !isSoccerSport(s) && !PRIORITY_SPORTS.includes(s))
      .sort((a, b) => formatSportLabel(a).localeCompare(formatSportLabel(b)));
    return [...priorityOrdered, ...rest];
  }, [knownSports]);

  // Individual soccer leagues for the secondary filter bar — derived
  // dynamically from whatever soccer_* sports the API currently reports.
  const soccerSubSports = React.useMemo(
    () => knownSports
      .filter(isSoccerSport)
      .sort((a, b) => formatSportLabel(a).localeCompare(formatSportLabel(b))),
    [knownSports]
  );

  const isSoccerModeActive = selectedSport === SOCCER_GROUP_VALUE || isSoccerSport(selectedSport);
  // ─── Debounce search query ─────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const validationError =
    sortBy === 'ev_state' && !stateFilter
      ? 'Select a state to sort by State EV.'
      : sortBy === 'ev_bookmaker' && !bookmakerFilter
        ? 'Select a bookmaker to sort by Bookmaker EV.'
        : null;

  const bestBetsQuery = useQuery({
    queryKey: ['value-finder', sortBy, sideFilter, marketFilter, stateFilter, bookmakerFilter, selectedSport, rankMode, dateFilter, page, debouncedSearch],
    queryFn: ({ signal }) => fetchBestBetsPageData({
      sortBy, sideFilter, marketFilter, stateFilter, bookmakerFilter, selectedSport, rankMode, dateFilter, page, debouncedSearch, signal,
    }),
    enabled: !validationError,
  });

  const rawData  = validationError ? [] : (bestBetsQuery.data?.valid ?? []);
  const hasMore  = validationError ? false : (bestBetsQuery.data?.hasMore ?? false);
  const loading  = !validationError && bestBetsQuery.isLoading;
  const error    = validationError || (bestBetsQuery.isError
    ? `Failed to load Value Finder.${typeof bestBetsQuery.error?.response?.data?.detail === 'string' ? ` ${bestBetsQuery.error.response.data.detail}` : ''}`
    : '');

  // ─── Toggle card expansion ─────────────────────────────────────────────────
  const toggleExpand = useCallback(id => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ─── Stable bet key ────────────────────────────────────────────────────────
  // Use player with team as fallback so h2h bets that store the team name in
  // `team` (not `player`) still produce a unique, stable key.
  const betKey = bet =>
    `${bet.game_id              || 'game'}-` +
    `${bet.player || bet.team  || ''}-` +
    `${bet.market               || 'market'}-` +
    `${bet.bet_type             || ''}-` +
    `${bet.point_line           ?? ''}-${bet.bookmaker || ''}`;

  // Deduplicate: if the API returns the same outcome more than once (identical
  // game / market / team / bookmaker combination) keep only the first occurrence.
  const bets = useMemo(() => {
    const seen = new Set();
    return rawData.filter(bet => {
      const k = betKey(bet);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [rawData]); // betKey is a stable fn defined in the same render scope

  const lockedOverlay = useMemo(
    () => buildLockedOverlay(ENTITLEMENT_KEYS.BEST_BETS_FULL),
    []
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <PageSeo seo={getPageSeo('value-finder', stateFilter)} />
      <div className="bb-page">

      {/* ── Top section: header + filter bar with premium aurora bg ── */}
      <div className="bb-top-section">

        {/* Aurora background layers (mirrors .ppbg in HomePage.css) */}
        <div className="bb-ppbg">
          <div className="bb-ppbg__aurora bb-ppbg__aurora--1" />
          <div className="bb-ppbg__aurora bb-ppbg__aurora--2" />
          <div className="bb-ppbg__aurora bb-ppbg__aurora--3" />
          <div className="bb-ppbg__aurora bb-ppbg__aurora--4" />
          <div className="bb-ppbg__noise" />
          <div className="bb-ppbg__grid" />
          <div className="bb-ppbg__vignette" />
          <div className="bb-ppbg__spotlight" />
        </div>

        {/* ── Page Header ─────────────────────────────────────────── */}
        <div className="bb-header">
          <div className="bb-header-text">
            <div className="bb-title-row">
              <h1 className="bb-title">Value Finder</h1>
              <Link
                to={`/academy?videoId=${BEST_BETS_VIDEO_ID}`}
                className="bb-learn-btn"
                aria-label="Watch the Value Finder walkthrough in the Academy"
              >
                Learn This Tool
              </Link>
            </div>
            <p className="bb-subtitle">
              Find the Best Odds & +EV Betting Opportunities. Top-value plays ranked by EV, grade &amp; market edge — updated live.
            </p>
          </div>
        </div>
      </div>{/* /bb-top-section */}

      {/* ── Sticky Filter Bar ───────────────────────────────────────── */}
      <div className="bb-filter-bar">

        {/* Search: Player or Team */}
        <div className="bb-filter-group">
          <label className="bb-filter-label">
            Search
            {!bestBetsAccess.allowed && <span className="bb-lock-inline" aria-label="Premium"> 🔒</span>}
          </label>
          <div className="bb-search-wrapper bb-search-wrapper--lockable">
            <input
              className="bb-search-input"
              type="text"
              value={searchQuery}
              disabled={!bestBetsAccess.allowed}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search player or team…"
            />
            <span
              className="bb-search-info"
              data-tip="Search for a player or team name. Results update automatically as you type."
              aria-label="Search info"
            >
              i
            </span>

            {/* Intercept click for non-subscribers to show upgrade prompt */}
            {!bestBetsAccess.allowed && (
              <button
                type="button"
                className="bb-lock-intercept"
                onClick={() => setShowSearchLock(true)}
                aria-label="Unlock player and team search — subscribe"
              />
            )}
          </div>

          {/* Upgrade overlay */}
          {showSearchLock && !bestBetsAccess.allowed && (
            <div className="bb-lock-overlay" role="dialog" aria-modal="true" aria-label="Premium feature">
              <button
                type="button"
                className="bb-lock-dismiss"
                onClick={() => setShowSearchLock(false)}
                aria-label="Close"
              >✕</button>
              <span className="bb-lock-icon" aria-hidden="true">🔒</span>
              <p className="bb-lock-msg">
                Unlock <strong>player &amp; team search</strong> with a Premium subscription.
              </p>
              <Link to="/subscribe" className="bb-lock-cta" onClick={() => setShowSearchLock(false)}>
                Upgrade to Premium →
              </Link>
            </div>
          )}
        </div>

        {/* Sport */}
        <div className="bb-filter-group bb-filter-group--sport">
          <label className="bb-filter-label">Sport</label>

          <select
            className="bb-select bb-select--sport"
            value={selectedSport}
            onChange={e => {
              setSelectedSport(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All Sports</option>
            {orderedSports.map(sport => (
              <option key={sport} value={sport}>
                {formatSportLabel(sport)}
              </option>
            ))}
          </select>

          {/* Secondary filter: individual soccer leagues, shown only when the
              consolidated Soccer group is selected. Options are derived from
              soccerSubSports (live API data), never hardcoded. */}
          {isSoccerModeActive && soccerSubSports.length > 0 && (
            <div className="bb-sport-scroll bb-soccer-subfilter" role="group" aria-label="Soccer league">
              <button
                type="button"
                className={`bb-toggle-btn${selectedSport === SOCCER_GROUP_VALUE ? ' active' : ''}`}
                onClick={() => {
                  setSelectedSport(SOCCER_GROUP_VALUE);
                  setPage(1);
                }}
              >
                All Soccer
              </button>
              {soccerSubSports.map(sport => (
                <button
                  key={sport}
                  type="button"
                  className={`bb-toggle-btn${selectedSport === sport ? ' active' : ''}`}
                  onClick={() => {
                    setSelectedSport(sport);
                    setPage(1);
                  }}
                >
                  {formatSportLabel(sport)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Side */}
        <div className="bb-filter-group bb-filter-group--side">
          <label className="bb-filter-label">Side</label>
          <div className="bb-toggle-group">
            {['all', 'over', 'under'].map(s => (
              <button
                key={s}
                className={`bb-toggle-btn ${sideFilter === s ? 'active' : ''}`}
                onClick={() => { setSideFilter(s); setPage(1); }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Market */}
        <div className="bb-filter-group bb-filter-group--market">
          <label className="bb-filter-label">Market</label>
          <select
            className="bb-select"
            value={marketFilter}
            onChange={e => { setMarketFilter(e.target.value); setPage(1); }}
          >
            {MARKET_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Sort */}
        <div className="bb-filter-group bb-filter-group--sort">
          <label className="bb-filter-label">Sort</label>
          <select
            className="bb-select"
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setPage(1); }}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Rank By: EV (default) / Bet Grade (signal_score) / Date */}
        <div className="bb-filter-group bb-filter-group--rank">
          <label className="bb-filter-label">Rank By</label>
          <div className="bb-toggle-group">
            {RANK_MODES.map(o => (
              <button
                key={o.value}
                type="button"
                className={`bb-toggle-btn ${rankMode === o.value ? 'active' : ''}`}
                onClick={() => { setRankMode(o.value); setPage(1); }}
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Day picker — only visible in 'date' rank mode */}
          {rankMode === 'date' && (
            <div className="bb-sport-scroll bb-date-subfilter" role="group" aria-label="Date">
              {DATE_QUICK_OPTIONS.map(o => (
                <button
                  key={o.value}
                  type="button"
                  className={`bb-toggle-btn${dateFilter === o.value ? ' active' : ''}`}
                  onClick={() => { setDateFilter(o.value); setPage(1); }}
                >
                  {o.label}
                </button>
              ))}
              <input
                type="date"
                className="bb-date-input"
                aria-label="Pick an exact date"
                value={/^\d{4}-\d{2}-\d{2}$/.test(dateFilter) ? dateFilter : ''}
                onChange={e => {
                  if (e.target.value) {
                    setDateFilter(e.target.value);
                    setPage(1);
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* State picker — only visible in ev_state mode */}
        {sortBy === 'ev_state' && (
          <div className="bb-filter-group bb-filter-group--dep">
            <label className="bb-filter-label">State</label>
            <select
              className="bb-select"
              value={stateFilter}
              onChange={e => {
                const val = e.target.value;
                setStateFilter(val);
                setPage(1);
                if (val) {
                  navigate(`/value-finder/${val.toLowerCase()}`, { replace: true });
                }
              }}
            >
              <option value="" disabled>Select a state…</option>
              {[
                ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
                ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
                ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
                ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],
                ['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
                ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
                ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
                ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
                ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
                ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
                ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],
                ['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
                ['WI','Wisconsin'],['WY','Wyoming'],['DC','Washington D.C.'],
              ].map(([abbr, name]) => (
                <option key={abbr} value={abbr}>{name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Bookmaker picker — only visible in ev_bookmaker mode */}
        {sortBy === 'ev_bookmaker' && (
          <div className="bb-filter-group bb-filter-group--dep">
            <label className="bb-filter-label">
              Bookmaker
              {!bestBetsAccess.allowed && <span className="bb-lock-inline" aria-label="Premium"> 🔒</span>}
            </label>
            <div className="bb-select-wrap bb-select-wrap--lockable">
              <select
                className="bb-select"
                value={bookmakerFilter}
                disabled={!bestBetsAccess.allowed}
                onChange={e => { setBookmakerFilter(e.target.value); setPage(1); }}
              >
                {bestBetsAccess.allowed
                  ? BOOKMAKERS.map(({ label, key }) => (
                      <option key={key} value={key}>{label}</option>
                    ))
                  : <option value="draftkings">DraftKings</option>
                }
              </select>

              {/* Intercept click for non-subscribers to show upgrade prompt */}
              {!bestBetsAccess.allowed && (
                <button
                  type="button"
                  className="bb-lock-intercept"
                  onClick={() => setShowBookLock(true)}
                  aria-label="Unlock all sportsbooks — subscribe"
                />
              )}
            </div>

            {/* Upgrade overlay */}
            {showBookLock && !bestBetsAccess.allowed && (
              <div className="bb-lock-overlay" role="dialog" aria-modal="true" aria-label="Premium feature">
                <button
                  type="button"
                  className="bb-lock-dismiss"
                  onClick={() => setShowBookLock(false)}
                  aria-label="Close"
                >✕</button>
                <span className="bb-lock-icon" aria-hidden="true">🔒</span>
                <p className="bb-lock-msg">
                  Unlock <strong>all {BOOKMAKERS.length} sportsbooks</strong> with a Premium subscription.
                </p>
                <Link to="/subscribe" className="bb-lock-cta" onClick={() => setShowBookLock(false)}>
                  Upgrade to Premium →
                </Link>
              </div>
            )}
          </div>
        )}

      </div>{/* /bb-filter-bar */}


      {/* ── Content ───────────────────────────────────────────────────── */}
      {loading && (
        <div className="bb-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bb-card-skeleton">
              <div className="skel skel-badge" />
              <div className="skel skel-title" />
              <div className="skel skel-line" />
              <div className="skel skel-row" />
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="bb-error">{error}</div>
      )}

      {/* DB is rebuilding — raw API returned zero rows */}
      {!loading && !error && rawData.length === 0 && !debouncedSearch && (
        <RecalculatingState />
      )}

      {/* Search returned no results */}
      {!loading && !error && rawData.length === 0 && debouncedSearch && (
        <div className="bb-empty">
          <p>No bets found matching “{debouncedSearch}”.</p>
          <p className="bb-empty-hint">Try a different player or team name.</p>
        </div>
      )}

      {/* Filters produced no results, but the DB has data */}
      {!loading && !error && rawData.length > 0 && bets.length === 0 && (
        <div className="bb-empty">
          <p>No qualifying bets found with current filters.</p>
          <p className="bb-empty-hint">Try broadening the sport, type, or side filter.</p>
        </div>
      )}

      {!loading && !error && bets.length > 0 && (
        <div className="bb-cards-grid">
          {bets.map((bet, idx) => {
            // After dedup, betKey is unique across bets — use it directly as
            // the React key so React never mounts two cards for the same bet.
            const cardKey = betKey(bet);
            const isLocked = shouldLockPreviewItem({
              accessState: bestBetsAccess,
              index: idx,
            });

            if (isLocked) {
              return (
                <BestBetCard
                  key={cardKey}
                  bet={bet}
                  rank={idx + 1}
                  bookmakerIcons={BOOKMAKER_ICONS}
                  formatMarket={formatMarket}
                  fmtOdds={fmtOdds}
                  lockedOverlay={lockedOverlay}
                />
              );
            }
            return (
              <BestBetCard
                key={cardKey}
                bet={bet}
                rank={idx + 1}
                expanded={expandedIds.has(cardKey)}
                onToggle={() => toggleExpand(cardKey)}
                bookmakerIcons={BOOKMAKER_ICONS}
                formatMarket={formatMarket}
                fmtOdds={fmtOdds}
              />
            );
          })}
        </div>
      )}

      {/* ── Pagination ─────────────────────────────────────────────── */}
      {!loading && !error && !debouncedSearch && (page > 1 || hasMore) && (
          <div className="bb-pagination">
            <button
              className="bb-page-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ← Prev
            </button>
            <span className="bb-page-info">Page {page}</span>
            <button
              className="bb-page-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
            >
              Next →
            </button>
          </div>
        )}

    </div>
    </>
  );
}